import { AfterViewInit, Directive, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { Router } from '@angular/router';
import {
  ToolBar,
  IColumnArray,
  BaseFpxRoGridHandleAction,
  SearchType,
  ColumnType,
  SortType,
  BaseFpxDataService,
  SortSearch,
  GridTransformFn,
  BaseFpxRoGridDisableAction,
  CriteriaQuery,
  RowActionArray,
} from '@fpx/core';
import { BehaviorSubject, Observable, Subject, of, take, takeUntil } from 'rxjs';

@Directive()
export class BaseFpxROGridComponent<T, Helper extends BaseFpxRoGridHelper>
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() refreshCriteria$: Observable<CriteriaQuery> = of(new CriteriaQuery());

  public refresh$ : Observable<CriteriaQuery>;
  
  _gridWidth!: number;
  private _gridHeaders: string[] = [];
  private _columnWidths: number[] = [];
  private _columnSortSearch: Map<string, SortSearch> = new Map();
  private _isSortSearch: Map<
    string,
    'sort' | 'search' | 'sort&search' | undefined
  > = new Map();
  private _gridIdentifiers: string[] = [];
  private _gridCell: Map<string, GridTransformFn<any>> = new Map();
  private _count: number = 10;
  private _offset: number = 1;
  gridService!: BaseFpxDataService<any>;
  toolBar: ToolBar[] = [];
  gridData: T[] = [];
  selectedRow: T | undefined;
  protected _helper: Helper;
  private _router : Router;
  title: string = '';
  _columnType: ColumnType[] = [];
  paginationRequired: boolean = true;
  pageSize: number = 10;

  columnsArray: IColumnArray<T>[] = [];
  routingParams : any;
  private _destroy$ : Subject<any> = new Subject();
  protected interceptors : Map<'postFindall',any> = new  Map<'postFindall',any>();

  constructor(_helper: Helper, _router : Router) {
    this._helper = _helper; 
    this._router = _router;
    this.setRoutingParam();
  } 

  setRoutingParam(){
    this.routingParams =  this._router.getCurrentNavigation()?.extras?.queryParams;
  }

  setPageSize(pageSize: number) {
    this.pageSize = pageSize;
  }

  addSortSearch(
    columnName: string,
    isSearchable: boolean,
    isSortable: boolean
  ): void {
    let action: 'sort' | 'search' | 'sort&search' | undefined = undefined;
    if (isSearchable && isSortable) {
      action = 'sort&search';
    } else if (isSearchable) {
      action = 'search';
    } else if (isSortable) {
      action = 'sort';
    }

    this._isSortSearch.set(columnName, action);
  }

  setGridWidth(gridWidth: number) {
    this._gridWidth = gridWidth;
  }
  setPagination(isPaginationRequired: boolean): void {
    this.paginationRequired = isPaginationRequired;
  }

  setGridColumnTypes(columnType: ColumnType[]) {
    this._columnType = columnType;
  }

  setGridHeaders(headerNames: string[]): void {
    this._gridHeaders = headerNames;
  }
  setColumnWidths(columnWidths: number[]): void {
    this._columnWidths = columnWidths;
  }
  setGridIdentifiers(identifiers: string[]): void {
    this._gridIdentifiers = identifiers;
  }
  setGridCell(key: string, value: (element: T) => string): void {
    this._gridCell.set(key, value);
  }
  addGridDataService(gridDataServiceFn: BaseFpxDataService<any>): void {
    this.gridService = gridDataServiceFn;
  }
  addTransformation(column: string, transformFn: GridTransformFn<T>): void {
    // this.transformMap.set(column,transformFn);
    this._gridCell.set(column, transformFn);
  }

  addToolBar(
    type: 'icon' | 'button',
    key: string,
    name: string,
    hoverText = '',
    required? : BaseFpxRoGridDisableAction
  ): void {
    this.toolBar.push({ type, key, name, hoverText, required });
  }

  setGridTitle(title: string): void {
    this.title = title;
  }

  handleActions({ name, data }: { name: string; data: any }) {
    this._helper.notifyToolBarHandleActions(name, data);
  }

  ngOnInit(): void {
    this.doPreInit();
    this._helper.refreshGrid$ = new Subject();
    this.refresh$ = this._helper.refreshGrid$.asObservable();
    this._helper._routingParams = this.routingParams;
    this.toolBar = this._helper.getToolBar();
    this._isSortSearch = this._helper.getSortSearch();
    this._gridCell = this._helper.getTransformMap();
    this._gridWidth = this._helper.getGridWidth();
    this._columnWidths = this._helper.getGridColumnWidth();
    for (let i = 0; i < this._gridIdentifiers.length; i++) {
      if (this._isSortSearch.has(this._gridIdentifiers[i])) {
        this._columnSortSearch.set(this._gridIdentifiers[i], {
          action: this._isSortSearch.get(this._gridIdentifiers[i]),
          columnType: this._columnType[i],
          columnSortType:
            this._isSortSearch.get(this._gridIdentifiers[i]) === 'sort' ||
            this._isSortSearch.get(this._gridIdentifiers[i]) === 'sort&search'
              ? 'asc'
              : undefined,
          searchType:
            this._isSortSearch.get(this._gridIdentifiers[i]) === 'search' ||
            this._isSortSearch.get(this._gridIdentifiers[i]) === 'sort&search'
              ? this.getDefaultFilter(this._columnType[i])
              : undefined,
          searchText: '',
          fromValue: '',
          toValue: '',
        });
      }
    }
    for (let index = 0; index < this._gridHeaders.length; index++) {
      const columns: IColumnArray<any> = {
        action: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.action,
        columnDef: this._gridIdentifiers[index] ?? '',
        header: this._gridHeaders[index] ?? '',
        cell: this._gridCell.has(this._gridIdentifiers[index])
          ? this._gridCell.get(this._gridIdentifiers[index])
          : undefined,
        width: this._columnWidths[index] ?? 'auto',
        columnType: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.columnType,
        searchType: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.searchType,
        sortType: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.columnSortType,
        searchText:
          this._columnSortSearch.get(this._gridIdentifiers[index])
            ?.searchText ?? '',
        fromValue: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.fromValue,
        toValue: this._columnSortSearch.get(this._gridIdentifiers[index])
          ?.toValue,
      };
      this.columnsArray.push(columns);
    }
    console.log(this.columnsArray);
    this._helper.doPreInit();
    this._setInterceptors();

  }

  private _setInterceptors(){
    this.interceptors.set('postFindall',this._helper.postFindallInterceptor)
  }

  ngAfterViewInit(){
    this.doPostInit();
    this._helper.doPostInit();
    this.refreshCriteria$.pipe(takeUntil(this._destroy$)).subscribe(
      (criteria:CriteriaQuery) => {
        this._helper.refreshGrid(criteria);
      }
    );
  }

  getDefaultFilter(columnType: ColumnType): SearchType {
    return columnType === 'String' ? 'Contains' : 'equals';
  }

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
    this.doDestroy();
    this._helper.doDestroy()
  }

  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected doDestroy(): void {}
}

export abstract class BaseFpxRoGridHelper {

  private destroy$ : Subject<any> = new Subject()
  private _criteriaQuery : CriteriaQuery = new CriteriaQuery();
  public _routingParams : any;
  public  refreshGrid$ : Subject<CriteriaQuery> ;
  private _toolBarhandleActionsMap: Map<String, BaseFpxRoGridHandleAction> =
    new Map();
  
  public rowActionArray:RowActionArray[] = [];

  protected addHandleActions(
    name: string,
    toolBarhandleActions: BaseFpxRoGridHandleAction
  ): void {
    this._toolBarhandleActionsMap.set(name, toolBarhandleActions);
  }

  public notifyToolBarHandleActions(name: string, data: any) {
    if (this._toolBarhandleActionsMap.has(name)) {
      (this._toolBarhandleActionsMap as Map<string, any>).get(name)(name, data);
    }
  }

  public abstract getToolBar(): ToolBar[];
  public abstract getSortSearch(): Map<
    string,
    'sort' | 'search' | 'sort&search' | undefined
  >;
  public abstract getTransformMap(): Map<string, GridTransformFn<any>>;
  public abstract getGridWidth(): number;
  public abstract getGridColumnWidth() : number[] ;

  public setCriteriaQuery(criteriaQuery : CriteriaQuery){
    this._criteriaQuery = criteriaQuery;
  }

  public getCriteriaQuery():CriteriaQuery{
    return this._criteriaQuery
  }

  public refreshGrid(criteriaQuery:CriteriaQuery){
    this.refreshGrid$.next(criteriaQuery);
  }

  protected getRoutingParam(key: string) {
    return this._routingParams[key] || "";
  }

  public doPreInit(): void {}
  public doPostInit(): void {}

  doDestroy(){
    this.rowActionArray = [];
    this.destroy$.next(null);
    this.destroy$.complete()
  }

  public postFindallInterceptor = (payload:any) : any => {
    console.log('post findAll interceptor called');
    console.log(payload);
    return payload
  }

  public addRowAction(action:string, rowActionhandle: BaseFpxRoGridHandleAction, option: {name:string, cssClass:string}) {
    let _option:any = {
      ...option,
      action: action
    }
    this.rowActionArray.push(_option);
    this._toolBarhandleActionsMap.set(action, rowActionhandle);
  }

}
